"use client"

import { X, Minus, Plus } from "lucide-react"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import ProductCard from "../components/ProductCard"
import summaryApi from "../common/index"
import Context from "../context/index"
import { useEffect, useState, useContext } from "react"
import { toast } from "react-hot-toast"
import { useParams } from "react-router-dom"
//import { toast } from "../hooks/use-toast"
import { Link } from "react-router-dom";


const Sidebar = ({ isCartOpen, onClose }) => {
  const [cartItems, setCartItems] = useState([])
  const [loadingCart, setLoadingCart] = useState(false)
  const params = useParams()
  const [wishlistedItems, setWishlistedItems] = useState(new Set())
  // const [loadingWishlist, setLoadingWishlist] = useState(true) // loadingWishlist not used, only setter
  const [allProducts, setAllProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(true)

  const { fetchUserAddToCart } = useContext(Context)
  // const { fetchUserWishlistCount } = useContext(Context) // Not currently used

  const fetchCartItems = async () => {
    try {
      setLoadingCart(true)
      const response = await fetch(summaryApi.addToCartProductView.url, {
        method: summaryApi.addToCartProductView.method,
        credentials: "include",
      })

      const data = await response.json()
      if (data.success) {
        setCartItems(data.data || [])
      }
    } catch (err) {
      toast.error("Failed to load cart items")
    } finally {
      setLoadingCart(false)
    }
  }

  const updateCartQuantity = async (cartItemId, newQuantity) => {
    if (newQuantity < 1) return

    try {
      const response = await fetch(summaryApi.updateCartProduct.url, {
        method: summaryApi.updateCartProduct.method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          _id: cartItemId,
          quantity: newQuantity,
        }),
      })

      const data = await response.json()
      if (data.success) {
        toast.success("Cart updated successfully")
        fetchCartItems()
        fetchUserAddToCart()
      } else {
        toast.error("Failed to update cart")
      }
    } catch (err) {
      toast.error("Failed to update cart")
    }
  }


const handleAddToCart = async (productId, size) => {
  if (!productId) return;
  try {
    const res = await fetch(summaryApi.addToCartProduct.url, {
      method: summaryApi.addToCartProduct.method,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ productId, size }),
    });

    const data = await res.json();

    if (data.success) {
      fetchCartItems();       // refresh sidebar cart
      fetchUserAddToCart();   // refresh global cart count
    } else {
      toast.error(data.message || "Failed to add product to cart");
    }
  } catch (err) {
    toast.error("Something went wrong");
  }
};



  const removeFromCart = async (cartItemId) => {
    try {
      const response = await fetch(summaryApi.deleteCartProduct.url, {
        method: summaryApi.deleteCartProduct.method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          _id: cartItemId,
        }),
      })

      const data = await response.json()
      if (data.success) {
        toast.success("Item removed from cart")
        fetchCartItems()
        fetchUserAddToCart()
      } else {
        toast.error("Failed to remove item")
      }
    } catch (err) {
      toast.error("Failed to remove item")
    }
  }

  const fetchAllProducts = async () => {
    try {
      setLoadingProducts(true)
      const res = await fetch(summaryApi.allProduct.url)
      const data = await res.json()
      setAllProducts(data?.data || [])
    } catch (err) {
      console.error("Failed to fetch products:", err)
    } finally {
      setLoadingProducts(false)
    }
  }

  const fetchWishlist = async () => {
  try {
    const res = await fetch(summaryApi.getWishlistProducts.url, {
      method: summaryApi.getWishlistProducts.method,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });

    const data = await res.json();

    if (data.success) {
      const wishlistIds = new Set(data.data.map((item) => item.productId));
      setWishlistedItems(wishlistIds);
    }
  } catch (err) {
    toast.error("Failed to load wishlist");
  } finally {
    // setLoadingWishlist(false); // Not currently used
  }
};

  useEffect(() => {
    fetchWishlist()
    fetchAllProducts()
    if (isCartOpen) {
      fetchCartItems()
    }
  }, [params, isCartOpen])

  const subtotal = cartItems.reduce((sum, item) => {
    const price = item.productId?.priceRs || 0
    return sum + price * item.quantity
  }, 0)

 const handleWishlistToggle = async (productId) => {
  if (!productId) return;

  try {
    const isWishlistedNow = wishlistedItems.has(productId);
    const url = isWishlistedNow ? summaryApi.removeWishlistProduct.url : summaryApi.addToWishlist.url;
    const method = isWishlistedNow ? summaryApi.removeWishlistProduct.method : summaryApi.addToWishlist.method;

    const res = await fetch(url, {
      method,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ productId }),
    });

    const data = await res.json();

    if (data.success) {
      toast.success(
        isWishlistedNow
          ? "Product removed from wishlist"
          : "Product added to wishlist"
      );

      // Optional: reload or update state instead of full reload
      window.location.reload();
    } else {
      toast.error("Could not update wishlist");
    }
  } catch (err) {
    toast.error("Could not update wishlist");
  }
};


  if (!isCartOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-300">
          <h2 className="text-lg font-bold text-gray-900">YOUR SHOPPING CART ({cartItems.length})</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 text-gray-800">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4">
            {loadingCart ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Loading cart...</p>
              </div>
            ) : cartItems.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">Your cart is empty</p>
                <p className="text-sm text-gray-400">Add some items to get started!</p>
              </div>
            ) : (
              cartItems.map((item) => {
                const product = item.productId
                const isMinusDisabled = item.quantity <= 1

                return (
                  <div key={item._id} className="flex gap-4 border-b border-gray-200 pb-4">
                    <div className="w-20 h-20 bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
                      <img
                        src={product?.productImages?.[0]?.url || "/placeholder.svg"}
                        alt={product?.productName || "Product"}
                        className="w-full h-full object-contain"
                      />
                    </div>

                   <div className="flex-1 min-w-0">
  <h3 className="font-semibold text-gray-900 text-sm mb-1">
    {product?.productName || "Product"}
  </h3>
  
  <p className="text-sm text-gray-700 mb-2">
    Brand: {product?.brandName || "N/A"}
  </p>

  {/* Quantity Controls */}
  <div className="flex items-center gap-2">
    <Button
      variant="outline"
      size="icon"
      className={`h-8 w-8 ${isMinusDisabled ? "cursor-not-allowed text-gray-400 border-gray-200" : "text-black hover:text-black"}`}
      onClick={() => !isMinusDisabled && updateCartQuantity(item._id, item.quantity - 1)}
      disabled={isMinusDisabled}
    >
      <Minus className="h-3 w-3" />
    </Button>

    <Input
      type="number"
      value={item.quantity}
      onChange={(e) => {
        const newQty = Number.parseInt(e.target.value) || 1;
        if (newQty > 0) {
          updateCartQuantity(item._id, newQty);
        }
      }}
      className="w-16 h-8 text-center text-gray-900 font-semibold"
      min="1"
    />

    <Button
      variant="outline"
      size="icon"
      className="h-8 w-8 text-black hover:text-black bg-transparent"
      onClick={() => updateCartQuantity(item._id, item.quantity + 1)}
    >
      <Plus className="h-3 w-3" />
    </Button>

    <Button
      variant="outline"
      size="icon"
      className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 bg-transparent"
      onClick={() => removeFromCart(item._id)}
      title="Remove from cart"
    >
      <X className="h-3 w-3" />
    </Button>
  </div>
</div>


                    <div className="text-right">
                      <p className="font-bold text-gray-900">
                        £ 
                        {((product?.priceRs || 0) * item.quantity).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* All Products Section */}
   <div className="border-t border-gray-300 bg-gray-50 p-4">
  {loadingProducts ? (
    <div className="flex flex-col justify-center items-center py-10">
      <div className="sparkle-loader relative mb-4">
        <div className="golden-rings">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div className="sparkles">
          <div className="sparkle"></div>
          <div className="sparkle"></div>
          <div className="sparkle"></div>
          <div className="sparkle"></div>
        </div>
      </div>
      <p className="loader-text text-lg font-semibold text-yellow-400 animate-pulse">
        Loading products… ✨
      </p>
    </div>
  ) : (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {allProducts.map((prod) => (
        <div key={prod._id} className="flex flex-col h-full">
          <ProductCard
            product={prod}
            isWishlisted={wishlistedItems.has(prod._id)}
            onWishlistToggle={handleWishlistToggle}
            onAddToCart={handleAddToCart} // real add-to-cart
          />
        </div>
      ))}
    </div>
  )}
</div>




        </div>

      {/* Footer */}
<div className="border-t border-gray-300 p-4 space-y-4">
  <div className="flex justify-between items-center">
    <span className="font-semibold text-gray-800">SUBTOTAL:</span>
    <span className="font-bold text-lg text-gray-900">
      £ {subtotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
    </span>
  </div>

  <Link to="/checkout" onClick={onClose}>
    <Button
      className="w-full bg-gray-900 hover:bg-black text-white py-3 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
      disabled={cartItems.length === 0} // Disable if cart is empty
    >
      CHECKOUT
    </Button>
  </Link>
</div>




      </div>
    </>
  )
}

export default Sidebar
